import { useState, useEffect, useRef, useMemo } from 'react';
import { Widget } from 'jamespot-front-business';
import { AnyRTMessage, jObjectBase, Namespaces, RTMessageHandler } from 'jamespot-user-api';
import socketObject from '../../utils/socket';
import { useAppDispatch } from '../../redux/store';

export function useComponentVisible(initialIsVisible = false) {
    const [isComponentVisible, setIsComponentVisible] = useState(initialIsVisible);
    const ref = useRef(null);

    const handleClickOutside = (event: any) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (ref.current && !ref.current.contains(event.target)) {
            setIsComponentVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    return { ref, isComponentVisible, setIsComponentVisible };
}

export type FitType = Record<'type', string[]>;

export function fitConditions(conditions: FitType[], o: jObjectBase) {
    let passCondition = !conditions.length;
    conditions.forEach((cond) => {
        Object.keys(cond).map((key) => {
            const k = key as keyof Pick<jObjectBase, 'type'>;
            if (key in o && cond[k].includes(o[k])) {
                passCondition = true;
            }
        });
    });
    return passCondition;
}

export function fitAudience(audience: string[], messageAudience: string[]) {
    let passCondition = false;
    audience.forEach((uri) => {
        passCondition = messageAudience.includes(uri);
    });
    return passCondition;
}

export const useArticleRT = (
    conditions: FitType[],
    audience: string[],
    fn: Array<Namespaces['JAMESPOT']> = ['article-delete', 'article-update', 'article-create'],
) => {
    const [rtObjects, setRTObjects] = useState<jObjectBase[]>([]);
    const socket = useMemo(() => socketObject, []);
    const rtHandlers = useMemo<Array<RTMessageHandler<'JAMESPOT', Namespaces['JAMESPOT']>>>(() => {
        const articleCreateHandler = function (message: AnyRTMessage) {
            if (message.namespace === 'JAMESPOT' && fn.includes(message.function)) {
                const doFitCondition = !conditions.length || fitConditions(conditions, message.object);
                const doFitAudience = !audience.length || fitAudience(audience, message.audience ?? []);
                if (doFitCondition && doFitAudience) {
                    setRTObjects((prevState) => [...prevState, message.object]);
                }
            }
        };
        return [
            {
                namespace: 'JAMESPOT',
                function: 'article-create',
                handler: articleCreateHandler,
            },
        ];
    }, [conditions, audience, fn]);

    useEffect(() => {
        rtHandlers.forEach((handler) => {
            socket.onMessage(handler.namespace, handler.function, handler.handler);
        });
        return () => {
            rtHandlers.forEach((handler) => {
                socket.offMessage(handler.namespace, handler.function, handler.handler);
            });
        };
    }, [rtHandlers, socket]);

    return { rtObjects, rtLastObject: rtObjects.at(-1) };
};

export const useWidgetRT = (uniqid: string) => {
    const dispatch = useAppDispatch();
    const socket = useMemo(() => socketObject, []);
    const rtHandlers = useMemo(() => Widget.getWidgetRTHandlers(dispatch, uniqid), [dispatch, uniqid]);

    useEffect(() => {
        rtHandlers.forEach((handler) => {
            socket.onMessage(handler.namespace, handler.function, handler.handler);
        });
        return () => {
            rtHandlers.forEach((handler) => {
                socket.offMessage(handler.namespace, handler.function, handler.handler);
            });
        };
    }, [rtHandlers, socket]);
};
